import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { usePaginationFragment, usePreloadedQuery, useQueryLoader } from "react-relay";
import { Link } from "react-router-dom";
import { graphql } from "relay-runtime";

import { DeviceConnectivityHealthTag } from "@skydio/rivet-entities";
import { Button, Input, Paragraph, useDebouncedState } from "@skydio/rivet-ui";

import ConfirmUnclaimDeviceModal, {
  UnclaimBehaviour,
} from "components/common/ConfirmUnclaimDeviceModal";
import OrgNameUuidSelectFilter from "components/common/filters/OrgNameUuidFilter";
import ReleaseMaxSelectFilter from "components/common/filters/ReleaseMaxFilter";
import VehicleTypeVehicleCategoryFilter from "components/common/filters/VehicleTypeVehicleCategoryFilter";
import { legacyToRivetPagination, useRelayPagination } from "components/common/Pagination";
import PaginatedTable from "components/common/table/PaginatedTable";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { SplitStringArraySerializer, useQueryStringState } from "hooks/useQueryStringState";
import { formatOrgPrefixedPath } from "utils/common";

import { VehicleTypeCategoriesContext } from "../../common/VehicleTypeCategories";
import AdminSearchEmptyState from "./AdminSearchEmptyState";
import { UnclaimDeviceMenuItem } from "./UnclaimDeviceMenuItem";

import styles from "../AdminSearchPage.module.scss";

import type { ColumnType } from "antd/lib/table";
import type { FC } from "react";
import type { PreloadedQuery } from "react-relay";
import type { ConnectionNodeType } from "@skydio/relay-toolkit";
import type { Key } from "@skydio/rivet-ui";
import type {
  VehiclesList_vehicles$data,
  VehiclesList_vehicles$key,
} from "__generated__/VehiclesList_vehicles.graphql";
import type {
  VehiclesListAdminQuery,
  vehicletype,
} from "__generated__/VehiclesListAdminQuery.graphql";
import type { VehiclesRefetchQuery } from "__generated__/VehiclesRefetchQuery.graphql";

export const rootQuery = graphql`
  query VehiclesListAdminQuery(
    $count: Int!
    $cursor: String
    $searchTerm: String
    $organizationUuid: String
    $releaseUuid: String
    $vehicleTypes: [vehicletype!]
  ) {
    ...VehiclesList_vehicles
  }
`;

const vehiclesFragment = graphql`
  fragment VehiclesList_vehicles on Query @refetchable(queryName: "VehiclesRefetchQuery") {
    vehicles(
      searchTerm: $searchTerm
      organizationUuid: $organizationUuid
      releaseUuid: $releaseUuid
      vehicleTypes: $vehicleTypes
      first: $count
      after: $cursor
    ) @connection(key: "VehiclesList_vehicles") {
      __id
      totalCount
      edges {
        node {
          id
          __id
          uuid
          name
          displayName
          skydioSerial
          vehicleId
          organizationId
          vehicleType
          organization {
            name
          }
          release {
            releaseKey
            name
            versionString
          }
          isOnline
          deviceHealthStatus
          dock {
            displayName
            dockId
          }
          accessPoint {
            displayName
            skydioSerial
          }
        }
      }
    }
  }
`;

export interface TableProps {
  queryReference: PreloadedQuery<VehiclesListAdminQuery>;
}

const PAGE_SIZE = 10;
export type Vehicle = ConnectionNodeType<VehiclesList_vehicles$data["vehicles"]>;

const VehicleStatusCell: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => {
  const healthStatus = vehicle.deviceHealthStatus || "UNKNOWN";

  const deviceData = useMemo(
    () => ({
      id: vehicle.id,
      isOnline: vehicle.isOnline,
      deviceHealthStatus: healthStatus,
    }),
    [vehicle.id, vehicle.isOnline, healthStatus]
  );

  return <DeviceConnectivityHealthTag device={deviceData} />;
};

interface VehicleActionCellProps {
  record: ConnectionNodeType<VehiclesList_vehicles$data["vehicles"]>;
  onUnclaim: (deviceNodeId: string, displayName: string) => void;
}

const VehicleActionCell: FC<VehicleActionCellProps> = React.memo(function VehicleActionCell({
  record,
  onUnclaim,
}) {
  const handleUnclaim = useCallback(
    () => onUnclaim(record.id, record.displayName || record.vehicleId || ""),
    [onUnclaim, record]
  );

  return <UnclaimDeviceMenuItem handler={handleUnclaim} />;
});

VehicleActionCell.displayName = "VehicleActionCell";

export interface VehiclesPageContextType {
  setCount?: (count: number | undefined) => void;
}
export const VehiclesPageContext = createContext<VehiclesPageContextType | null>(null);
const EMPTY_LIST: string[] = [];
const VehiclesList: React.FC<{
  queryReference: PreloadedQuery<VehiclesListAdminQuery>;
}> = ({ queryReference }) => {
  const vehiclesPageContext = useContext(VehiclesPageContext);

  const queryData = usePreloadedQuery<VehiclesListAdminQuery>(rootQuery, queryReference);
  const enableVehicleDeviceSettingsPage = useFeatureFlag("enableVehicleDeviceSettingsPage");

  const [searchTerm, setSearchTerm] = useQueryStringState("search", "");
  const [localSearchTerm, setLocalSearchTerm] = useQueryStringState("lsearch", "");
  const [vehicleNameSerialIdInput, handleVehicleNameSerialIdInput] = useDebouncedState<string>(
    localSearchTerm,
    setLocalSearchTerm
  );

  useEffect(() => {
    if (localSearchTerm) {
      setSearchTerm("");
    }
  }, [localSearchTerm, setSearchTerm]);

  const [organizationUuid, setOrganizationUuid] = useState<Key | null>(null);
  const [releaseUuid, setReleaseUuid] = useState<Key | null>(null);

  const [vehicleTypes, setVehicleTypes] = useQueryStringState<string[]>(
    "v",
    EMPTY_LIST,
    SplitStringArraySerializer
  );

  const paginationQuery = usePaginationFragment<VehiclesRefetchQuery, VehiclesList_vehicles$key>(
    vehiclesFragment,
    queryData
  );

  const [paginationProps, currentPageEdges, resetPage] = useRelayPagination(
    paginationQuery,
    PAGE_SIZE,
    paginationQuery.data?.vehicles
  );

  const { refetch } = paginationQuery;
  const [isPending, startTransition] = useTransition();

  const filteredVehicleTypes = useMemo(() => {
    const v = vehicleTypes.filter(type => type !== "");
    return v.length === 0 ? null : (v as vehicletype[]);
  }, [vehicleTypes]);

  useEffect(() => {
    startTransition(() => {
      resetPage();

      let count = PAGE_SIZE;
      if (
        !localSearchTerm &&
        !searchTerm &&
        !organizationUuid &&
        !releaseUuid &&
        (!filteredVehicleTypes || filteredVehicleTypes?.length == 0)
      )
        count = 0;
      refetch({
        count: count,
        searchTerm: localSearchTerm ? localSearchTerm : searchTerm, // Prefer local searchTerm
        organizationUuid: organizationUuid as string,
        releaseUuid: releaseUuid as string,
        vehicleTypes: filteredVehicleTypes,
      });
    });
  }, [
    refetch,
    resetPage,
    searchTerm,
    organizationUuid,
    releaseUuid,
    filteredVehicleTypes,
    localSearchTerm,
  ]);

  const currentPageNodes = currentPageEdges.map(edge => edge.node).filter(node => node !== null);

  const resetFilters = useCallback(() => {
    setLocalSearchTerm("");
    setSearchTerm("");
    setOrganizationUuid(null);
    setReleaseUuid(null);
    setVehicleTypes([]);
    handleVehicleNameSerialIdInput("");
  }, [handleVehicleNameSerialIdInput, setLocalSearchTerm, setSearchTerm, setVehicleTypes]);

  const [unclaimModalState, setUnclaimModalState] = useState<{
    visible: boolean;
    deviceNodeId: string;
    displayName: string;
  } | null>(null);
  const handleCloseModal = useCallback(() => setUnclaimModalState(null), []);
  const handleUnclaim = useCallback((deviceNodeId: string, displayName: string) => {
    setUnclaimModalState({ visible: true, deviceNodeId, displayName });
  }, []);

  const connectionIds = useMemo(() => {
    const id = paginationQuery.data?.vehicles?.__id;
    return id ? [id] : [];
  }, [paginationQuery.data?.vehicles?.__id]);

  const getColumns = useCallback((): ColumnType<Vehicle>[] => {
    return [
      {
        title: "Vehicle Type",
        dataIndex: "vehicleType",
        render: (value: string | null) => <Paragraph>{value}</Paragraph>,
      },
      {
        title: "Name",
        render: (vehicle: Vehicle) => (
          <Paragraph>
            {vehicle.displayName &&
              (vehicle.organizationId && enableVehicleDeviceSettingsPage ? (
                <Link
                  to={formatOrgPrefixedPath(
                    vehicle.organizationId,
                    `/settings/devices/vehicles/${vehicle.vehicleId}`
                  )}
                >
                  {vehicle.displayName}
                  <div className="text-xs">{vehicle.skydioSerial}</div>
                </Link>
              ) : (
                <Paragraph>{vehicle.displayName}</Paragraph>
              ))}
          </Paragraph>
        ),
      },
      {
        title: "Vehicle ID",
        dataIndex: "vehicleId",
        render: (value: string | null) => <Paragraph>{value}</Paragraph>,
      },
      {
        title: "Organization",
        render: (vehicle: Vehicle) => (
          <Paragraph>
            {vehicle.organizationId && (
              <Link to={formatOrgPrefixedPath(vehicle.organizationId, "/settings/general")}>
                {vehicle.organization?.name}
              </Link>
            )}
          </Paragraph>
        ),
      },
      {
        title: "Release",
        render: (vehicle: Vehicle) => (
          <>
            <Paragraph>{vehicle?.release?.versionString}</Paragraph>
            <div className="text-xs">{vehicle?.release?.releaseKey}</div>
          </>
        ),
      },
      {
        title: "Status",
        render: (vehicle: Vehicle) => <VehicleStatusCell vehicle={vehicle} />,
      },
      {
        title: "Linked Devices",
        render: (vehicle: Vehicle) => (
          <>
            <Paragraph>
              {vehicle.organizationId && vehicle.dock && (
                <Link
                  to={formatOrgPrefixedPath(
                    vehicle.organizationId,
                    `/settings/devices/docks/${vehicle.dock.dockId}`
                  )}
                >
                  {vehicle.dock.displayName}
                </Link>
              )}
            </Paragraph>
            <Paragraph>
              {vehicle.organizationId && vehicle.accessPoint && (
                <Link
                  to={formatOrgPrefixedPath(
                    vehicle.organizationId,
                    `/settings/devices/accessPoints/${vehicle.accessPoint.skydioSerial}`
                  )}
                >
                  {vehicle.accessPoint.displayName}
                </Link>
              )}
            </Paragraph>
          </>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => <VehicleActionCell record={record} onUnclaim={handleUnclaim} />,
      },
    ];
  }, [enableVehicleDeviceSettingsPage, handleUnclaim]);

  const columns = useMemo(() => getColumns(), [getColumns]);

  useEffect(() => {
    if (vehiclesPageContext?.setCount) {
      vehiclesPageContext?.setCount(
        paginationQuery.data.vehicles?.edges.length ? paginationProps.totalCount : 0
      );
    }
  }, [
    vehiclesPageContext,
    vehiclesPageContext?.setCount,
    paginationProps.totalCount,
    paginationQuery.data.vehicles?.edges.length,
  ]);

  const handleVehicleTypesSelectionChange = useCallback(
    (keys: Set<Key>) => {
      setVehicleTypes(Array.from(keys) as string[]);
    },
    [setVehicleTypes]
  );

  const vehicleTypeCategoriesContext = useContext(VehicleTypeCategoriesContext);
  const categories = vehicleTypeCategoriesContext?.categories;
  const vehicleVehicleTypes = useMemo(
    () =>
      categories?.vehicleTypeCategories?.vehicleTypes
        ? (Array.from(categories?.vehicleTypeCategories?.vehicleTypes) as vehicletype[])
        : [],
    [categories?.vehicleTypeCategories?.vehicleTypes]
  );

  return (
    <>
      {unclaimModalState && (
        <ConfirmUnclaimDeviceModal
          visible={unclaimModalState.visible}
          deviceNodeId={unclaimModalState.deviceNodeId}
          displayName={unclaimModalState.displayName}
          connectionIds={connectionIds}
          onClose={handleCloseModal}
          mode={UnclaimBehaviour.REFRESH}
        />
      )}
      <div className="flex w-full gap-4 pl-1">
        <VehicleTypeVehicleCategoryFilter
          selectedKeys={vehicleTypes}
          onSelectionChange={handleVehicleTypesSelectionChange}
        />
        <Input
          placeholder="Vehicle Name/Serial/ID"
          className={styles.search}
          value={vehicleNameSerialIdInput}
          onValueChange={handleVehicleNameSerialIdInput}
          isClearable
        />
        <OrgNameUuidSelectFilter
          selectedKey={organizationUuid}
          onSingleSelectionChange={setOrganizationUuid}
        />
        <ReleaseMaxSelectFilter
          selectedKey={releaseUuid}
          onSingleSelectionChange={setReleaseUuid}
          vehicleTypes={vehicleVehicleTypes}
        />
        <Button onClick={resetFilters} title="Reset filters">
          Reset
        </Button>
      </div>
      {!paginationQuery.data.vehicles || paginationQuery.data.vehicles.edges.length == 0 ? (
        <AdminSearchEmptyState
          hasResults={paginationProps.totalCount != 0}
          hasFilters={true}
          isLoading={isPending}
        />
      ) : (
        <PaginatedTable
          paginationProps={legacyToRivetPagination(paginationProps, resetPage)}
          columns={columns}
          dataSource={currentPageNodes}
          loading={isPending}
        />
      )}
    </>
  );
};

export const VehiclesPage = () => {
  const [queryReference, fetchVehicles] = useQueryLoader<VehiclesListAdminQuery>(rootQuery);
  useEffect(() => {
    fetchVehicles({ count: 0 });
  }, [fetchVehicles]);

  return queryReference && <VehiclesList queryReference={queryReference} />;
};

export default VehiclesList;
